import { useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreativeType } from '@marketing-milk/interfaces'
import { TextField } from '@material-ui/core'
import InputLabel from '@material-ui/core/InputLabel'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { Button, Popover } from '@mui/material'
import { ServerError } from 'components/server-request/server-error/ServerError'
import React, { useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useCreatives } from '../../useCreatives'
import { useOpenAd } from '../CreativeEditorModal/CreativeEditorModal'

type Props = {
  sprintId?: number
  children?: React.ReactElement
  open?: boolean
  onClose?: () => void
  anchorEl?: HTMLElement | null
}
export const NewCreativeModal = ({ children, sprintId, open, onClose, anchorEl }: Props) => {
  const { open: openAd } = useOpenAd()
  const queryClient = useQueryClient()
  const businessID = useGetBusinessID()
  const [name, setName] = useState<string>()
  const [error, setError] = useState<Error>()
  const [type, setType] = useState<AdCreativeType>()
  const [associatedSprintId, setAssociatedSprintId] = useState<number>()
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = open ?? internalOpen
  const close = () => {
    setInternalOpen(false)
    onClose?.()
  }
  const [isLoading, setLoading] = useState(false)
  const buttonRef = useRef<HTMLElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const { createCreative } = useCreatives(businessID)

  const onSubmit = async () => {
    try {
      setLoading(true)
      if (!name || !type) return

      const newCreative = await createCreative(name, type, {
        // @ts-ignore
        sprintId: sprintId,
      })

      // The new ad opens in the editor over the page it was made from, so the list
      // behind it has to learn about it too — `createCreative` only refreshes its
      // own query, which no page renders.
      queryClient.invalidateQueries(sprintId ? ['sprints', 'sprint-creatives'] : ['creatives'])
      openAd(newCreative.id)
    } catch (e: any) {
      setError(e)
    }

    setLoading(false)
    close()
  }

  return (
    <>
      {children && (
        <span className="cursor-pointer" ref={buttonRef} onClick={() => setInternalOpen(o => !o)}>
          {children}
        </span>
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl ?? buttonRef.current}
        onClose={close}
        ref={portalRef}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <div className="border-b">
          <div className="p-4 space-y-4">
            <TextField
              id="creative-name-input"
              label="Creative Name"
              inputProps={{ 'aria-label': 'creative name' }}
              placeholder="e.g. Free Trial Creative"
              fullWidth
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <div>
              <InputLabel shrink>Creative Type</InputLabel>
              <ToggleButtonGroup exclusive value={type} onChange={(e, value) => setType(value)}>
                <ToggleButton
                  value={AdCreativeType.single_image}
                  aria-label="single image"
                  data-testid="single-creative-type"
                >
                  Single Image / Video
                </ToggleButton>
                <ToggleButton
                  value={AdCreativeType.dynamic}
                  aria-label="dynamic"
                  data-testid="dynamic-creative-type"
                >
                  Dynamic
                </ToggleButton>
                <ToggleButton
                  value={AdCreativeType.carousel}
                  aria-label="carousel"
                  data-testid="carousel-creative-type"
                >
                  Carousel
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>
        </div>

        <div className="flex p-2 w-full  justify-between items-center">
          <Button onClick={close}>Cancel</Button>

          <Button variant="contained" color="primary" disabled={!name || !type} onClick={onSubmit}>
            <i className={`fa fa-${isLoading ? 'spinner fa-spin' : 'save'} mr-2 text-white`} />
            {isLoading ? 'Creating' : 'Save'}
          </Button>
        </div>
      </Popover>

      {error && (
        <ServerError error={error} message={error.message} onClose={() => setError(undefined)} />
      )}
    </>
  )
}
